// front/SubscriptionForm.jsx
import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation, Trans } from "react-i18next";
import ComicButton from "../../shared/components/ComicButton/ComicButton";
import { subscribe as apiSubscribe } from "../../core/api/api";

// Convierte la clave pública VAPID de base64 a Uint8Array
function urlBase64ToUint8Array(base64String) {
  if (!base64String) {
    throw new Error("No se proporcionó la clave VAPID pública.");
  }
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const SubscriptionForm = () => {
  const { t } = useTranslation();
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);
  const [showWarning, setShowWarning] = useState(false);  // Modal de aviso
  const [showEmailModal, setShowEmailModal] = useState(false);  // Modal para introducir email
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  // Saber si hay algún modal abierto
  const isModalOpen = showWarning || showEmailModal;

  // Cuando el usuario pulse "Suscribirse" en el formulario principal
  const handleSubscribeClick = () => {
    if (!acceptedPolicy) {
      setShowWarning(true);
      return;
    }
    setShowEmailModal(true);
  };

  // Cierra el modal de aviso
  const closeWarningModal = () => {
    setShowWarning(false);
  };

  // Cierra el modal de email
  const closeEmailModal = () => {
    setShowEmailModal(false);
  };

  // Lógica de suscripción (Push + Email)
  const subscribe = async () => {
    if (!email) {
      alert(t("subscribe.errorNoEmail"));
      return;
    }
    try {
      setLoading(true);

      // Suscripción Push
      if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.ready;
        const vapidPublicKey =
          process.env.REACT_APP_VAPID_PUBLIC_KEY ||
          "BKaF82OT7GXC5F6usbIe9xm74t4v32kS6HvQNh50xNGH8fUrpElW7qGH9Xr0KVBPS5GHVfoQ3rRr1f2xvPtnFZI";
        const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
        const pushSubscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });
        const subscriptionData = pushSubscription.toJSON();
        // Usar servicio API centralizado para push
        await apiSubscribe({ type: "push", ...subscriptionData });
      } else {
        console.warn("El navegador no soporta notificaciones push.");
      }

      // Suscripción Email usando servicio API centralizado
      const data = await apiSubscribe({ type: "email", email });
      alert(data.message || t("subscribe.success"));
      setShowEmailModal(false);
    } catch (error) {
      console.error("Error al suscribirse:", error);
      alert(t("subscribe.error"));
    } finally {
      setLoading(false);
    }
  };

  // Variantes de framer-motion para animaciones
  const modalVariants = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
  };

  return (
    // Contenedor principal con z-0
    <div className="relative z-0 flex flex-col items-center text-white">
      <h2 className="text-3xl font-bold mb-4">{t("subscribe.title")}</h2>
      <p className="max-w-md text-center mb-4">{t("subscribe.message")}</p>

      <div className="mb-6">
        <label className="inline-flex items-center">
          <input
            type="checkbox"
            className="mr-2"
            checked={acceptedPolicy}
            onChange={(e) => setAcceptedPolicy(e.target.checked)}
          />
          <Trans
            i18nKey="subscribe.checkbox"
            components={[
              <a
                key="privacyLink"
                href="/privacidad"
                target="_blank"
                rel="noopener noreferrer"
                className="text-yellow-400 underline ml-1"
              >
                política de privacidad
              </a>,
            ]}
          />
        </label>
      </div>

      <div className="flex items-center justify-center gap-4">
        <ComicButton
          buttonName={t("subscribe.buttonSubscribe")}
          hoverBtnName={t("subscribe.buttonSubscribe")}
          handleButtonClick={handleSubscribeClick}
        />
        <ComicButton
          buttonName={t("subscribe.buttonBack")}
          hoverBtnName={t("subscribe.buttonBack")}
          handleButtonClick={() => window.history.back()}
        />
      </div>

      {/* AnimatePresence para modales y overlay */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            className="absolute inset-0 z-10 bg-black bg-opacity-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          />
        )}

        {showWarning && (
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-20"
            variants={modalVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <div className="bg-gray-900 p-6 rounded-md max-w-sm w-full text-center relative">
              <h3 className="text-xl font-bold mb-4">{t("subscribe.warningTitle")}</h3>
              <p className="mb-4">{t("subscribe.warningMsg")}</p>
              <ComicButton
                buttonName="Aceptar"
                hoverBtnName="Aceptar"
                handleButtonClick={closeWarningModal}
              />
            </div>
          </motion.div>
        )}

        {showEmailModal && (
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-20"
            variants={modalVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <div className="bg-gray-900 p-6 rounded-md max-w-sm w-full text-center relative">
              <h3 className="text-xl font-bold mb-4">{t("subscribe.enterEmailTitle")}</h3>
              <input
                type="email"
                className="w-full p-2 mb-4 rounded bg-gray-800 border border-gray-700 focus:outline-none focus:border-yellow-500"
                placeholder={t("subscribe.emailPlaceholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <div className="flex items-center justify-center gap-4">
                <ComicButton
                  buttonName={t("subscribe.buttonSubscribe")}
                  hoverBtnName={t("subscribe.buttonSubscribe")}
                  handleButtonClick={subscribe}
                  disabled={loading}
                />
                <ComicButton
                  buttonName={t("subscribe.buttonBack")}
                  hoverBtnName={t("subscribe.buttonBack")}
                  handleButtonClick={closeEmailModal}
                  disabled={loading}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SubscriptionForm;
